import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    FlaskConical, Search, ListChecks, BookOpenCheck, 
    ArrowRight, Loader2, AlertTriangle, XCircle, 
    Beaker, Activity, ShoppingCart, Tag
} from 'lucide-react';
import Navbar from './Navbar';
import Fotter from './Fotter';

const labTestData = [
    { id: '1', name: 'Complete Blood Count (CBC)', description: 'Evaluates overall blood health.', actualPrice: 80, discountedPrice: 75, category: 'Hematology' },
    { id: '2', name: 'Basic Metabolic Panel (BMP)', description: 'Checks kidney function and electrolytes.', actualPrice: 110, discountedPrice: 100, category: 'Chemistry' },
    { id: '3', name: 'Lipid Panel', description: 'Measures cholesterol levels.', actualPrice: 130, discountedPrice: 120, category: 'Chemistry' },
    { id: '4', name: 'Thyroid Function Test (TFT)', description: 'Assesses thyroid gland function.', actualPrice: 100, discountedPrice: 90, category: 'Endocrinology' },
    { id: '5', name: 'Liver Function Test (LFT)', description: 'Evaluates liver health.', actualPrice: 95, discountedPrice: 85, category: 'Chemistry' },
    { id: '6', name: 'Urinalysis', description: 'Analyzes urine for various components.', actualPrice: 65, discountedPrice: 60, category: 'Urinalysis' },
    { id: '7', name: 'Comprehensive Metabolic Panel (CMP)', description: 'Extensive check of body\'s metabolism.', actualPrice: 165, discountedPrice: 150, category: 'Chemistry' },
    { id: '8', name: 'Hemoglobin A1c (HbA1c)', description: 'Monitors long-term blood sugar control.', actualPrice: 120, discountedPrice: 110, category: 'Endocrinology' },
    { id: '9', name: 'Prostate-Specific Antigen (PSA)', description: 'Screens for prostate cancer.', actualPrice: 140, discountedPrice: 130, category: 'Immunology' },
    { id: '10', name: 'Vitamin D Test', description: 'Measures Vitamin D levels.', actualPrice: 75, discountedPrice: 70, category: 'Endocrinology' },
    { id: '11', name: 'COVID-19 PCR Test', description: 'Detects current COVID-19 infection.', actualPrice: 160, discountedPrice: 150, category: 'Microbiology' },
    { id: '12', name: 'Allergy Panel', description: 'Identifies specific allergies.', actualPrice: 220, discountedPrice: 200, category: 'Immunology' },
];

const LabTestPage = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTests, setSelectedTests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState("All");

    useEffect(() => {
        const timer = setTimeout(() => setLoading(false), 1000);
        return () => clearTimeout(timer);
    }, []);

    const categories = useMemo(() => ["All", ...new Set(labTestData.map(t => t.category))], []);

    const filteredTests = labTestData.filter(test => {
        const matchesSearch = test.name.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = activeCategory === "All" || test.category === activeCategory;
        return matchesSearch && matchesCategory;
    });

    const handleSelectTest = (test) => {
        setSelectedTests(prev => 
            prev.find(t => t.id === test.id) ? prev.filter(t => t.id !== test.id) : [...prev, test]
        );
    };

    const totalActual = selectedTests.reduce((acc, t) => acc + t.actualPrice, 0);
    const totalDiscounted = selectedTests.reduce((acc, t) => acc + t.discountedPrice, 0);
    const totalSavings = totalActual - totalDiscounted;

    return (
        <div style={ui.pageWrapper}>
            <Navbar />
            
            {/* Animated Glow Elements */}
            <div style={ui.glowPrimary}></div>
            <div style={ui.glowSecondary}></div>

            <main style={ui.container}>
                <header style={ui.header}>
                    <motion.h1 
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                        style={ui.mainTitle}
                    >
                        Precision <span style={ui.accentText}>Diagnostics</span>
                    </motion.h1>
                    <p style={ui.subtitle}>Premium clinical insights from India's advanced diagnostic network.</p>

                    <div style={ui.toolbar}>
                        <div style={ui.searchBox}>
                            <Search size={20} color="#94a3b8" />
                            <input 
                                type="text" 
                                placeholder="Search by test name or category..." 
                                style={ui.searchInput}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <div style={ui.filterBar}>
                            {categories.map(cat => (
                                <button 
                                    key={cat} 
                                    onClick={() => setActiveCategory(cat)}
                                    style={activeCategory === cat ? ui.activePill : ui.pill}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </div>
                </header>

                <section style={ui.gridSystem}>
                    <div style={ui.cardGrid}>
                        {loading ? (
                            <div style={ui.loaderContainer}><Loader2 size={40} style={ui.spinner} /></div>
                        ) : (
                            <motion.div layout style={ui.gridInner}>
                                <AnimatePresence mode='popLayout'>
                                    {filteredTests.map((test) => (
                                        <TestCard 
                                            key={test.id} 
                                            test={test} 
                                            isSelected={!!selectedTests.find(t => t.id === test.id)}
                                            onSelect={() => handleSelectTest(test)}
                                        />
                                    ))}
                                </AnimatePresence>
                            </motion.div>
                        )}
                    </div>

                    {/* Next-Gen Command Hub (Sidebar) */}
                    <AnimatePresence>
                        {selectedTests.length > 0 && (
                            <motion.aside 
                                initial={{ opacity: 0, x: 50 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 50 }}
                                style={ui.sidebar}
                            >
                                <div style={ui.sidebarHeader}>
                                    <ShoppingCart size={20} />
                                    <h3>Order Summary</h3>
                                </div>
                                <div style={ui.testList}>
                                    {selectedTests.map(test => (
                                        <div key={test.id} style={ui.sideItem}>
                                            <span>{test.name}</span>
                                            <span>₹{test.discountedPrice}</span>
                                        </div>
                                    ))}
                                </div>
                                <div style={ui.sidebarFooter}>
                                    <div style={ui.priceRow}>
                                        <span>Total Value</span>
                                        <span style={{textDecoration: 'line-through', color: '#94a3b8'}}>₹{totalActual}</span>
                                    </div>
                                    <div style={ui.priceRow}>
                                        <span>Instant Savings</span>
                                        <span style={{color: '#10b981'}}>- ₹{totalSavings}</span>
                                    </div>
                                    <div style={ui.totalLine}>
                                        <span>Payable</span>
                                        <span style={{color: '#3b82f6'}}>₹{totalDiscounted}</span>
                                    </div>
                                    <button style={ui.bookButton}>
                                        Confirm Booking <ArrowRight size={18} />
                                    </button>
                                </div>
                            </motion.aside>
                        )}
                    </AnimatePresence>
                </section>
            </main>
            <Fotter />
        </div>
    );
};

const TestCard = ({ test, isSelected, onSelect }) => {
    const discountPercent = Math.round(((test.actualPrice - test.discountedPrice) / test.actualPrice) * 100);

    return (
        <motion.div 
            layout
            whileHover={{ y: -8, scale: 1.02 }}
            style={isSelected ? ui.cardActive : ui.card}
            onClick={onSelect}
        >
            <div style={ui.cardTop}>
                <div style={ui.iconBox}><Beaker size={20} color="#3b82f6" /></div>
                <div style={ui.discountBadge}>{discountPercent}% OFF</div>
            </div>
            
            <h3 style={ui.cardTitle}>{test.name}</h3>
            <p style={ui.cardDesc}>{test.description}</p>
            
            <div style={ui.cardFooter}>
                <div style={ui.priceStack}>
                    <span style={ui.actualPrice}>₹{test.actualPrice}</span>
                    <span style={ui.promoPrice}>₹{test.discountedPrice}</span>
                </div>
                <button style={isSelected ? ui.removeBtn : ui.addBtn}>
                    {isSelected ? <XCircle size={18} /> : 'Add'}
                </button>
            </div>
        </motion.div>
    );
};

const ui = {
    pageWrapper: {
        minHeight: '100vh', backgroundColor: '#0f172a', position: 'relative',
        overflowX: 'hidden', fontFamily: "'Poppins', sans-serif", color: '#f1f5f9'
    },
    glowPrimary: {
        position: 'absolute', top: '10%', left: '5%', width: '400px', height: '400px',
        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%)', filter: 'blur(60px)', pointerEvents: 'none'
    },
    glowSecondary: {
        position: 'absolute', bottom: '20%', right: '5%', width: '500px', height: '500px',
        background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)', filter: 'blur(80px)', pointerEvents: 'none'
    },
    container: { maxWidth: '1400px', margin: '0 auto', padding: '160px 20px 80px', position: 'relative', zIndex: 1 },
    header: { textAlign: 'center', marginBottom: '60px' },
    mainTitle: { fontSize: '4rem', fontWeight: '900', letterSpacing: '-2px', margin: 0 },
    accentText: { background: 'linear-gradient(to right, #3b82f6, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
    subtitle: { color: '#94a3b8', fontSize: '1.2rem', marginTop: '10px' },
    
    toolbar: { marginTop: '40px', display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' },
    searchBox: {
        width: '100%', maxWidth: '600px', backgroundColor: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.1)', borderRadius: '20px', padding: '12px 20px',
        display: 'flex', alignItems: 'center', gap: '15px', backdropFilter: 'blur(10px)'
    },
    searchInput: { background: 'none', border: 'none', outline: 'none', color: 'white', width: '100%', fontSize: '1rem' },
    filterBar: { display: 'flex', gap: '10px', overflowX: 'auto', padding: '10px', maxWidth: '100vw' },
    pill: { padding: '8px 20px', borderRadius: '30px', border: '1px solid #334155', background: 'none', color: '#94a3b8', cursor: 'pointer', transition: '0.3s' },
    activePill: { padding: '8px 20px', borderRadius: '30px', background: '#3b82f6', color: 'white', border: '1px solid #3b82f6', fontWeight: '600' },

    gridSystem: { display: 'flex', gap: '40px', alignItems: 'flex-start' },
    cardGrid: { flex: 1 },
    gridInner: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '25px' },
    
    card: {
        background: 'rgba(30, 41, 59, 0.5)', backdropFilter: 'blur(20px)', padding: '25px', borderRadius: '28px',
        border: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer', transition: 'all 0.3s ease'
    },
    cardActive: {
        background: 'rgba(59, 130, 246, 0.1)', padding: '25px', borderRadius: '28px',
        border: '2px solid #3b82f6', cursor: 'pointer', boxShadow: '0 0 30px rgba(59, 130, 246, 0.2)'
    },
    cardTop: { display: 'flex', justifyContent: 'space-between', marginBottom: '20px' },
    iconBox: { padding: '10px', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '14px' },
    discountBadge: { background: '#10b981', color: 'white', padding: '4px 12px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: '800' },
    cardTitle: { fontSize: '1.25rem', fontWeight: '700', marginBottom: '10px' },
    cardDesc: { color: '#94a3b8', fontSize: '0.9rem', lineHeight: '1.6', marginBottom: '25px', height: '45px', overflow: 'hidden' },
    cardFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    priceStack: { display: 'flex', flexDirection: 'column' },
    actualPrice: { fontSize: '0.85rem', color: '#64748b', textDecoration: 'line-through' },
    promoPrice: { fontSize: '1.4rem', fontWeight: '800', color: '#fbbf24' },
    addBtn: { padding: '8px 24px', borderRadius: '12px', background: '#f1f5f9', color: '#0f172a', fontWeight: '700', border: 'none', cursor: 'pointer' },
    removeBtn: { padding: '8px 12px', borderRadius: '12px', background: '#ef4444', color: 'white', border: 'none', cursor: 'pointer' },

    sidebar: {
        width: '380px', position: 'sticky', top: '120px', background: 'rgba(15, 23, 42, 0.8)',
        backdropFilter: 'blur(30px)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '32px',
        padding: '30px', display: 'flex', flexDirection: 'column', gap: '25px'
    },
    sidebarHeader: { display: 'flex', alignItems: 'center', gap: '15px', borderBottom: '1px solid #334155', paddingBottom: '15px' },
    testList: { maxHeight: '250px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' },
    sideItem: { display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: '#cbd5e1' },
    sidebarFooter: { borderTop: '1px solid #334155', paddingTop: '20px', display: 'flex', flexDirection: 'column', gap: '10px' },
    priceRow: { display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem' },
    totalLine: { display: 'flex', justifyContent: 'space-between', fontSize: '1.4rem', fontWeight: '800', margin: '10px 0' },
    bookButton: { 
        padding: '16px', borderRadius: '18px', background: '#3b82f6', color: 'white', 
        fontWeight: '700', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'center', gap: '10px' 
    },
    loaderContainer: { width: '100%', height: '400px', display: 'flex', justifyContent: 'center', alignItems: 'center' },
    spinner: { animation: 'spin 2s linear infinite' }
};

export default LabTestPage;